export const MEDICINE_SEARCH_MIN_CHARS = 2;
export const VEHICLE_SUFFIX_LENGTH = MEDICINE_SEARCH_MIN_CHARS;
export const CHASSIS_SUFFIX_LENGTH = MEDICINE_SEARCH_MIN_CHARS;
export const VEHICLE_SEARCH_MAX_CHARS = 80;
export const CHASSIS_SEARCH_MAX_CHARS = 40;

export const VEHICLE_SUFFIX_VALIDATION_MSG = "Type at least 2 characters.";
export const CHASSIS_SUFFIX_VALIDATION_MSG = "Type at least 2 characters.";

/** @deprecated Use VEHICLE_SEARCH_MAX_CHARS / CHASSIS_SEARCH_MAX_CHARS */
export const SEARCH_INPUT_MAX_CHARS = VEHICLE_SEARCH_MAX_CHARS;
/** @deprecated Find Vehicles no longer searches by mobile in the vehicle field */
export const MIN_SEARCH_CHARS = VEHICLE_SUFFIX_LENGTH;
/** @deprecated Find Vehicles no longer searches by mobile */
export const MIN_PHONE_SEARCH_DIGITS = 6;

export function digitsOnly(value) {
  return String(value || "").replace(/\D/g, "");
}

/** Medicine name field. Letters, numbers and common name characters. */
export function sanitizeVehicleSuffixInput(value) {
  return String(value || "")
    .replace(/[^a-zA-Z0-9\s.&+/'()-]/g, "")
    .slice(0, VEHICLE_SEARCH_MAX_CHARS);
}

/** Batch number field. */
export function sanitizeChassisSuffixInput(value) {
  return String(value || "")
    .replace(/[^a-zA-Z0-9\s.&+/'()-]/g, "")
    .toUpperCase()
    .slice(0, CHASSIS_SEARCH_MAX_CHARS);
}

/** @deprecated Use sanitizeVehicleSuffixInput */
export function sanitizeVehicleOrPhoneInput(value, maxLen = VEHICLE_SEARCH_MAX_CHARS) {
  return sanitizeVehicleSuffixInput(value).slice(0, maxLen);
}

/** @deprecated Mobile search removed from Find Vehicles */
export function isPhoneLikeSearch(_value) {
  return false;
}

export function getFindVehiclesValidationMessage(
  draftVehicle,
  draftChassis,
  preference = "vehicle"
) {
  const v = sanitizeVehicleSuffixInput(draftVehicle);
  const c = sanitizeChassisSuffixInput(draftChassis);
  const vPartial = v.length > 0 && v.length < MEDICINE_SEARCH_MIN_CHARS;
  const cPartial = c.length > 0 && c.length < MEDICINE_SEARCH_MIN_CHARS;

  if (!vPartial && !cPartial) return "";

  if (vPartial && cPartial) {
    return preference === "chassis"
      ? CHASSIS_SUFFIX_VALIDATION_MSG
      : VEHICLE_SUFFIX_VALIDATION_MSG;
  }
  if (vPartial) return VEHICLE_SUFFIX_VALIDATION_MSG;
  return CHASSIS_SUFFIX_VALIDATION_MSG;
}

/** Pick vehicle vs chassis when both suffix lengths are satisfied. */
export function resolveActiveSearch(draftVehicle, draftChassis, preference) {
  const v = sanitizeVehicleSuffixInput(draftVehicle).trim();
  const c = sanitizeChassisSuffixInput(draftChassis).trim();
  const vReady = v.length >= MEDICINE_SEARCH_MIN_CHARS;
  const cReady = c.length >= MEDICINE_SEARCH_MIN_CHARS;

  if (vReady && cReady) {
    return preference === "chassis"
      ? { mode: "chassis", normalized: c }
      : { mode: "vehicle", normalized: v };
  }
  if (vReady) return { mode: "vehicle", normalized: v };
  if (cReady) return { mode: "chassis", normalized: c };
  return null;
}

/** True when id is a persisted repo_cases row (numeric BIGINT or legacy 24-hex). Excel-only rows use `${batchId}-${rowIndex}`. */
export function isPersistedCaseId(id) {
  const s = String(id || "").trim();
  if (!s) return false;
  if (/^[1-9]\d{0,18}$/.test(s)) return true;
  return /^[a-f0-9]{24}$/i.test(s);
}

/** @deprecated Use isPersistedCaseId — name kept so existing imports keep working. */
export function isMongoCaseId(id) {
  return isPersistedCaseId(id);
}

export function compareFindVehicleLabels(a, b, mode = "vehicle") {
  return findVehicleLabel(a, mode).localeCompare(findVehicleLabel(b, mode), "en", {
    numeric: true,
    sensitivity: "base",
  });
}

function findVehicleLabelKey(mode = "vehicle") {
  return mode === "chassis" || mode === "chassisNumber" ? "chassisNumber" : "vehicleNumber";
}

function findVehicleLabel(item, mode = "vehicle") {
  if (item?.isMedicine) return String(item._id || item.id || "");
  return String(item?.[findVehicleLabelKey(mode)] || "")
    .replace(/\s+/g, "")
    .toUpperCase();
}

/** Keep the first row for each plate or chassis. Search results must not repeat a number. */
export function uniqueFindVehicleItems(items, mode = "vehicle") {
  const seen = new Set();
  const unique = [];
  for (const item of Array.isArray(items) ? items : []) {
    const label = findVehicleLabel(item, mode);
    if (!label || seen.has(label)) continue;
    seen.add(label);
    unique.push(item);
  }
  return unique;
}

export function sortFindVehicleItems(items, mode = "vehicle") {
  return [...(Array.isArray(items) ? items : [])].sort((a, b) =>
    compareFindVehicleLabels(a, b, mode)
  );
}

/** Column-major: first half down the left (A–Z), rest down the right (A–Z continues). */
export function splitResultsByColumn(items) {
  const list = Array.isArray(items) ? items : [];
  const leftCount = Math.ceil(list.length / 2);
  return {
    left: list.slice(0, leftCount),
    right: list.slice(leftCount),
  };
}

export const getItems = (response) => {
  if (Array.isArray(response?.data?.items)) return response.data.items;
  if (Array.isArray(response?.items)) return response.items;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

export const safeValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return value;
};

export const getFullAddress = (item) => {
  const parts = [
    item.addressLine1,
    item.addressLine2,
    item.city,
    item.state,
    item.pincode,
  ].filter(Boolean);

  return parts.length ? parts.join(", ") : "-";
};

export const getShareMessage = (item) => {
  return `Vehicle Confirmation Request

Customer Name: ${safeValue(item.customerName)}
Mobile Number: ${safeValue(item.mobileNumber)}
Alternate Mobile Number: ${safeValue(item.alternateMobileNumber)}
Vehicle Number: ${safeValue(item.vehicleNumber)}
Engine Number: ${safeValue(item.engineNumber)}
Chassis Number: ${safeValue(item.chassisNumber)}
Reference Number: ${safeValue(item.referenceNumber)}
Vehicle Brand: ${safeValue(item.vehicleBrand)}
Vehicle Model: ${safeValue(item.vehicleModel)}
Full Address: ${getFullAddress(item)}
City: ${safeValue(item.city)}
State: ${safeValue(item.state)}
Pincode: ${safeValue(item.pincode)}
Loaded details: ${safeValue(item.loadedDetail || item.loadedShort)}
`;
};
